import React, { useContext, useEffect, useState } from 'react'
import { useSearchParams, useNavigate, useParams  } from 'react-router-dom';
import { useMediaQuery } from 'react-responsive';
import { deleteAccountByToken, removeFromPool } from '../services/CandidateService';
import useErrorPage from '../hooks/useErrorPage';
import { ResponseModel } from '../models/ResponseModel';
import { ReactComponent as SuccessfullyApplied } from '../assets/svg/successfully-applied.svg'
import { ReactComponent as InvalidApplied } from '../assets/svg/invalid-applied.svg'
import { StyleContext } from '../providers/StyleContextProvider';
import { ConfirmationLoader } from '../components/Loaders/ConfirmationLoader';
import { Header } from '../components/Shared/Header';
import { Button } from '../components/Shared/Buttons/Button';
import { Transition } from '../components/Shared/Transition';
import { Helmet } from 'react-helmet-async';
import { Footer } from '../components/Shared/Footer';
import { GetInTouch } from '../components/Shared/GetInTouch';

export const DeleteAccountResponsePage: React.FC = () => {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const { type } = useParams<{ type: string }>();
    const [loading, setLoading] = useState<boolean>(true);
    const [success, setSuccess] = useState<boolean>(false);
    const [message, setMessage] = useState<string>('');
    const styleContext = useContext(StyleContext);
    const [styles, setStyles] = useState<any>(styleContext.getComponentStyle("confirmationPage"));    
    const isRemoveFromPool = type === "pool";
    const title = isRemoveFromPool ? "Remove from Candidate Pool" : "Delete Account";        
    const isMobile = useMediaQuery({ query: "(max-width: 786px)" });
    const [toErrorPage] = useErrorPage();

    useEffect(() => {
        setStyles(styleContext.getComponentStyle("confirmationPage"));
    }, [isMobile]);

    useEffect(() => {
        if (isRemoveFromPool) {
            const organizationId = searchParams.get('organizationId') ?? '';
            const id  = searchParams.get('id')  ?? '';
            const key = searchParams.get('key') ?? '';
            if (!organizationId || !id || !key) {
                setMessage('Invalid or missing pool removal link.');
                setLoading(false);
                return;
            }
            handleRemoveFromPool(organizationId, id, key);
        } 
        else {
            const email = searchParams.get('email') ?? '';
            const token = searchParams.get('token') ?? '';
            if (!email || !token) {
                setMessage('Invalid or missing account deletion link.');
                setLoading(false);
                return;
            }
            handleDeleteAccount(email, token);
        }
    }, [searchParams]);

    async function handleDeleteAccount(email: string, token: string) {
        setLoading(true);
        await deleteAccountByToken(email, token)
        .then((response: ResponseModel) => {                
            setSuccess(response.status);
            setMessage(response.message);                
        })
        .catch((error) => toErrorPage(error))
        .finally(() => setLoading(false));
    }

    async function handleRemoveFromPool(organizationId: string, id: string, key: string) {
        setLoading(true);
        await removeFromPool(organizationId, id, key)
        .then((response: ResponseModel) => {              
            setSuccess(response.status);
            setMessage(response.message);                
        })
        .catch((error) => toErrorPage(error))
        .finally(() => setLoading(false));
    }

    return (
        <>
            <Helmet><title>{title} - Xpressjobs</title></Helmet>
            <Header styleSheet="lowHeader" searchEnable={false} heading={title} />
            <div style={styles.container}>
                {loading ? ( <ConfirmationLoader /> ) : (
                    <Transition>
                        {success ? <SuccessfullyApplied style={styles.image} /> : <InvalidApplied style={styles.image} /> }
                        <h3 style={{ ...styles.title, ...styles.title.checkout }}>
                            {success ? (isRemoveFromPool ? "Removed Successfully" : "Account Deleted Successfully") : (isRemoveFromPool ? "Removal Could Not Be Completed" : "Account Deletion Failed")}
                        </h3>
                        <div style={styles.description}>{message}</div>
                        <div style={styles.buttonsContainer}>
                            <Button style={{ ...styles.button, ...styles.button.back }} onClick={() => navigate('/')}> GO BACK TO HOME </Button>
                        </div>
                    </Transition>
                )}
            </div>
            <GetInTouch />
            <Footer />
        </>
    );
};
